import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';

import { ADDONS } from '#common/constants/addons';
import { EnsureSubscriptionComponent } from '#bootstrap/utils';
import EnhancedAutocomplete from '#components/EnhancedAutocomplete';
import useForm from '#utils/use-form';
import {
  useSaveUserMutation,
} from '#services/user';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { userValidations } from '#common/validation';
import { getRoleList } from '#common/feature';
import { USER_ROLES, REGIONS } from '#common/constants';
import makeid from '#utils/make-id';
import { formatDate } from '#common/utils';
import {
  useData,
  getInitialState,
} from './Edit.utils';

const PASSWORD_LENGTH = 10;

interface AuthUser {
  id?: string | number;
  username?: string;
  isAdmin?: boolean;
  isSalesperson?: boolean;
  isCustomer?: boolean;
  isStaff?: boolean;
}

interface CollectionOption {
  id: number | string;
  availableForAll?: boolean;
  availableForAllReadonly?: boolean;
  [key: string]: unknown;
}

interface CustomerOption {
  id?: number | string;
  relatedSalespersonId?: string | number;
  collectionRestrictionIds?: Array<string | number>;
  [key: string]: unknown;
}

interface SalespersonOption {
  id?: number | string;
  username?: string;
  collectionRestrictionIds?: Array<string | number>;
  [key: string]: unknown;
}

export default function UserEdit() {
  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const currentUsername = useSelector((state: { auth: { userInfo: { username: string } } }) => state.auth.userInfo.username);
  const currentUserId = useSelector((state: { auth: { userInfo: { id: number } } }) => state.auth.userInfo.id);
  const defaultCreateBy = `${currentUsername}#${currentUserId.toString().padStart(4, '0')}`;

  const currentUser = useSelector((state: { auth: { userInfo: AuthUser } }) => state.auth.userInfo);
  const availableRoles = getRoleList(currentUser, window.subscriptions);

  const {
    values,
    setValues,
    isValid,
    errors,
    errorMessages,
    handleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
  } = useForm({
    initialState: getInitialState({
      password: makeid(8),
    }),
    validations: userValidations as Parameters<typeof useForm>[0]['validations'],
  });

  useData({ resetForm });

  const location = useLocation();
  const navigate = useNavigate();

  const userInfo = useSelector((state: { auth: { userInfo: AuthUser } }) => state.auth.userInfo);
  const setDisableShippingInfo = (disableShippingInfo: boolean) => setValues({ disableShippingInfo });
  const setDisableBillingInfo = (disableBillingInfo: boolean) => setValues({ disableBillingInfo });
  const setRole = (role: string) => setValues({ role });

  const [saveUser] = useSaveUserMutation();

  const isEdit = !!location.search.match(/userId=(\S+)/i);

  const handleGeneratePassword = () => {
    const password = makeid(PASSWORD_LENGTH);
    handleChange(null, { name: 'password', value: password });
    setValues({
      passwordResetByUser: false,
      password_hash_bcrypt: undefined,
      forceAdminPasswordReset: true,
    });
  };

  const handleSaveUser = (formValues: Record<string, unknown>, formIsValid: boolean) => {
    if (!formIsValid) {
      return;
    }

    showBackdrop();

    const postData = { ...formValues };
    delete postData.availableCollections;
    delete postData.availableCustomers;

    void saveUser(postData)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `User "${String(postData.username)}" was successfully saved.`);
        if (hasError) {
          return;
        }

        if (isEdit) {
          // edit mode
          void navigate(location.pathname.replace('/edit', '/list'));
        } else {
          // add new mode
          resetForm(getInitialState({
            password: makeid(8),
            ...(res.data as Record<string, unknown>),
          }));
        }
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveUser);

  const availableCollections = values.availableCollections as CollectionOption[];
  const availableSalesperson = values.availableSalesperson as SalespersonOption[];
  const salespersonCustomerIds = values.salespersonCustomerIds as Array<string | number>;
  const collectionRestrictionIds = values.collectionRestrictionIds as Array<string | number>;

  // only for salesperson
  // same logic in validation/users.js
  const collectionForAllIds = availableCollections
    .filter((c) => (c.availableForAll || c.availableForAllReadonly))
    .map((c) => c.id);
  const getCustomerDisabled = (customer: CustomerOption) => {
    const existsSalesperson = availableSalesperson.some(
      (sp) => sp.id === customer.relatedSalespersonId,
    );
    if (existsSalesperson) {
      // relateSalespersonId exists, check the saleperson
      return true;
    }

    if (customer.id !== undefined && salespersonCustomerIds.includes(customer.id)) {
      // disable already selected
      return true;
    }

    // remove for all collection from collectionRestrictionIds
    const pureSalespersonCollectionIds = collectionRestrictionIds
      .filter((cid) => !collectionForAllIds.includes(cid));

    const pureCustomerCollectionIds = (customer.collectionRestrictionIds || [])
      .filter((cid) => !collectionForAllIds.includes(cid));

    return pureCustomerCollectionIds
      .some((ccid) => !pureSalespersonCollectionIds.includes(ccid));
  };

  // only for customer
  // same logic in validation/users.js
  const getSalespersonDisabled = (salesperson: SalespersonOption) => {
    // remove for all collection from collectionRestrictionIds
    const pureSalespersonCollectionIds = (salesperson.collectionRestrictionIds || [])
      .filter((cid) => !collectionForAllIds.includes(cid));

    const pureCustomerCollectionIds = collectionRestrictionIds
      .filter((cid) => !collectionForAllIds.includes(cid));

    return pureCustomerCollectionIds
      .some((ccid) => !pureSalespersonCollectionIds.includes(ccid));
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        {isEdit ? 'Edit' : 'Add'}
        {' '}
        User
      </Box>

      <FormControl sx={{ mb: 1, minWidth: 120 }} size="small">
        <InputLabel id="select-role">Role</InputLabel>
        <Select
          labelId="select-role"
          name="role"
          label="Role"
          value={values.role}
          disabled={isEdit}
          onChange={(event) => {
            const selectedRole = event.target.value as string;
            setRole(selectedRole);
            if (selectedRole !== 'customer') {
              setDisableShippingInfo(true);
              setDisableBillingInfo(true);
            }
          }}
        >
          {availableRoles.map((name) => (
            <MenuItem
              key={name}
              value={name.toLowerCase()}
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ ml: 1, minWidth: 160 }} size="small">
        <InputLabel id="select-region">Region</InputLabel>
        <Select
          labelId="select-region"
          name="region"
          label="Region"
          value={values.region}
          onChange={(e) => handleChange(e)}
        >
          {/* Empty value shown for existing users whose region was never set,
              so the Select renders cleanly rather than showing a blank chip. */}
          {!values.region && <MenuItem value=""><em>Not set</em></MenuItem>}
          {REGIONS.map(({ code, label }) => (
            <MenuItem key={code} value={code}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <TextField
          required
          fullWidth
          size="small"
          name="username"
          label="Store Name"
          value={values.username}
          error={!!errors.username}
          onChange={handleChange}
          helperText={errorMessages.username as string}
        />
        <TextField
          disabled
          size="small"
          name="code"
          label="Account Code"
          value={values.code}
          error={false}
        />
      </Box>
      {values.passwordResetByUser === true ? (
        <Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            Password set by user (not visible to admins)
          </Alert>
          <Button variant="text" onClick={handleGeneratePassword}>
            Reset to Admin-Generated Password
          </Button>
        </Box>
      ) : (
        <Box>
          <TextField
            required
            disabled
            size="small"
            name="password"
            label="Password"
            value={values.password}
            error={!!errors.password}
            onChange={handleChange}
          />
          <Button variant="text" onClick={handleGeneratePassword}>Click to regenerate</Button>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2">
          Contact Info
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          <TextField
            required
            fullWidth
            size="small"
            name="name"
            label="Name"
            value={values.name}
            error={!!errors.name}
            onChange={handleChange}
            helperText={errorMessages.name as string}
          />
          <TextField
            required
            fullWidth
            size="small"
            name="email"
            label="Email"
            value={values.email}
            error={!!errors.email}
            onChange={handleChange}
            helperText={errorMessages.email as string}
          />
          <TextField
            required
            fullWidth
            size="small"
            name="phone"
            label="Phone"
            value={values.phone}
            error={!!errors.phone}
            onChange={handleChange}
            helperText={errorMessages.phone as string}
          />
          <TextField
            required
            fullWidth
            size="small"
            name="address"
            label="Address"
            value={values.address}
            error={!!errors.address}
            onChange={handleChange}
            helperText={errorMessages.address as string}
            placeholder="Street Address, City, STATE ZIP (e.g., 60 Arron Smith Drive, Honolulu, HI 96814)"
          />
        </Paper>
      </Box>

      {(values.role === USER_ROLES.CUSTOMER) && (
        <>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                lineHeight: 3,
              }}
            >
              Shipping Info (Customers Only)
              <FormControlLabel
                control={(
                  <Checkbox
                    name="disableShippingInfo"
                    checked={!!values.disableShippingInfo}
                    onChange={(e) => handleChange(e)}
                  />
                )}
                label={<Typography variant="subtitle2">Same as Contact Info</Typography>}
              />
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, pb: 1, mb: 1 }}>
              <TextField
                required
                fullWidth
                size="small"
                name="shippingName"
                label="Name"
                disabled={!!values.disableShippingInfo}
                value={values.shippingName}
                error={!!errors.shippingName}
                onChange={handleChange}
                helperText={errorMessages.shippingName as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="shippingEmail"
                label="Email"
                disabled={!!values.disableShippingInfo}
                value={values.shippingEmail}
                error={!!errors.shippingEmail}
                onChange={handleChange}
                helperText={errorMessages.shippingEmail as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="shippingPhone"
                label="Phone"
                disabled={!!values.disableShippingInfo}
                value={values.shippingPhone}
                error={!!errors.shippingPhone}
                onChange={handleChange}
                helperText={errorMessages.shippingPhone as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="shippingAddress"
                label="Address"
                disabled={!!values.disableShippingInfo}
                value={values.shippingAddress}
                error={!!errors.shippingAddress}
                onChange={handleChange}
                helperText={errorMessages.shippingAddress as string}
              />
            </Paper>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                lineHeight: 3,
              }}
            >
              Billing Info (Customers Only)
              <FormControlLabel
                control={(
                  <Checkbox
                    name="disableBillingInfo"
                    checked={!!values.disableBillingInfo}
                    onChange={(e) => handleChange(e)}
                  />
                )}
                label={<Typography variant="subtitle2">Same as Contact Info</Typography>}
              />
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, pb: 1, mb: 1 }}>
              <TextField
                required
                fullWidth
                size="small"
                name="billingName"
                label="Name"
                disabled={!!values.disableBillingInfo}
                value={values.billingName}
                error={!!errors.billingName}
                onChange={handleChange}
                helperText={errorMessages.billingName as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="billingEmail"
                label="Email"
                disabled={!!values.disableBillingInfo}
                value={values.billingEmail}
                error={!!errors.billingEmail}
                onChange={handleChange}
                helperText={errorMessages.billingEmail as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="billingPhone"
                label="Phone"
                disabled={!!values.disableBillingInfo}
                value={values.billingPhone}
                error={!!errors.billingPhone}
                onChange={handleChange}
                helperText={errorMessages.billingPhone as string}
              />
              <TextField
                required
                fullWidth
                size="small"
                name="billingAddress"
                label="Address"
                disabled={!!values.disableBillingInfo}
                value={values.billingAddress}
                error={!!errors.billingAddress}
                onChange={handleChange}
                helperText={errorMessages.billingAddress as string}
              />
            </Paper>
          </Box>
        </>
      )}

      {([USER_ROLES.CUSTOMER, USER_ROLES.SALESPERSON].includes(values.role as string)) && (
        <Box>
          <EnhancedAutocomplete
            multiple
            disableCloseOnSelect
            size="small"
            data-testid="collection-restrictions"
            error={!!errors.collectionRestrictionIds}
            helperText={errorMessages.collectionRestrictionIds as string}
            options={availableCollections as unknown as { id: number; name: string }[]}
            // props for enhanced
            label="Collection Restrictions"
            selectedIdsName="collectionRestrictionIds"
            selectedIds={collectionRestrictionIds as number[]}
            onChange={(e, changes) => handleChange(e as Parameters<typeof handleChange>[0], changes)}
          />

          <EnsureSubscriptionComponent
            addonId={ADDONS.SALESPERSON}
            alsoEnsure={(values.role === USER_ROLES.CUSTOMER && !!userInfo.isAdmin)}
          >
            <Box>
              <FormControl fullWidth sx={{ mb: 1, minWidth: 240 }} size="small">
                <InputLabel id="select-related-salesperson">Related Salesperson</InputLabel>
                <Select
                  labelId="select-related-salesperson"
                  name="relatedSalespersonId"
                  label="Related Salesperson"
                  value={values.relatedSalespersonId || ''}
                  onChange={(e) => handleChange(e)}
                >
                  {availableSalesperson.map((sp) => (
                    <MenuItem
                      key={sp.id}
                      value={sp.id}
                      disabled={getSalespersonDisabled(sp)}
                    >
                      {sp.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </EnsureSubscriptionComponent>
        </Box>
      )}

      <EnsureSubscriptionComponent
        addonId={ADDONS.SALESPERSON}
        alsoEnsure={(values.role === USER_ROLES.SALESPERSON)}
      >
        <Box>
          <EnhancedAutocomplete
            multiple
            disableCloseOnSelect
            size="small"
            data-testid="salesperson-customers"
            options={values.availableCustomers as { id: number; name: string }[]}
            error={!!errors.salespersonCustomerIds}
            helperText={errorMessages.salespersonCustomerIds as string}
            getOptionDisabled={getCustomerDisabled}
            // props for enhanced
            label="Salesperson Customers"
            selectedIdsName="salespersonCustomerIds"
            selectedIds={salespersonCustomerIds as number[]}
            onChange={(e, changes) => handleChange(e as Parameters<typeof handleChange>[0], changes)}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2">
            Commission Fee
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
            <TextField
              required
              fullWidth
              size="small"
              type="number"
              name="sampleItemsPercent"
              label="Sample Items (%)"
              value={values.sampleItemsPercent}
              error={!!errors.sampleItemsPercent}
              onChange={handleChange}
              helperText={errorMessages.sampleItemsPercent as string}
            />
            <TextField
              required
              fullWidth
              size="small"
              type="number"
              name="reorderItemsPercent"
              label="Reorder Items (%)"
              value={values.reorderItemsPercent}
              error={!!errors.reorderItemsPercent}
              onChange={handleChange}
              helperText={errorMessages.reorderItemsPercent as string}
            />
          </Paper>
        </Box>
      </EnsureSubscriptionComponent>

      <TextField
        required
        fullWidth
        disabled
        size="small"
        name="createdBy"
        label="Created By"
        value={values.createdBy || defaultCreateBy}
      />

      <TextField
        fullWidth
        disabled
        size="small"
        name="createdDate"
        label="Created Date"
        value={formatDate(values.createdDate as string, '-')}
      />

      <Button
        type="button"
        variant="contained"
        size="small"
        disabled={!isValid}
        onClick={handleSubmit}
      >
        Save
      </Button>
    </>
  );
}
